import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, Button, LoadingState, ErrorState, Logo } from "@/components/design-system";
import { mockConnectPublic, mockConnectComplete } from "@/lib/payments";
import { formatApiError } from "@/lib/api";

export default function MockConnectOnboarding() {
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "loading", err: "" });
  const [account, setAccount] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const a = await mockConnectPublic(accountId);
        setAccount(a);
        setState({ status: "ready", err: "" });
      } catch (err) {
        setState({ status: "error", err: formatApiError(err) });
      }
    })();
  }, [accountId]);

  const finish = async (outcome) => {
    setBusy(outcome);
    try {
      if (outcome === "complete") {
        await mockConnectComplete(accountId);
      }
      const targetUrl = outcome === "complete" ? searchParams.get("return_url") : searchParams.get("refresh_url");
      if (targetUrl) {
        const local = targetUrl.startsWith(window.location.origin);
        if (local) {
          nav(targetUrl.replace(window.location.origin, ""));
        } else {
          window.location.assign(targetUrl);
        }
      } else {
        nav("/app/payouts");
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(null);
    }
  };

  if (state.status === "loading") return <LoadingState label="Loading onboarding…" />;
  if (state.status === "error") return <ErrorState detail={state.err} />;

  return (
    <div className="min-h-screen bg-[#E8F1FF] flex items-center justify-center p-6">
      <div className="w-full max-w-lg space-y-4">
        <div className="flex items-center justify-center gap-3">
          <Logo variant="light" height={40} />
          <span className="text-[11px] uppercase tracking-widest text-[#0B1F3A]/60">Mock Stripe Express</span>
        </div>
        <Card data-testid="mock-connect" className="!p-8">
          <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#2D7FF9]">Simulated onboarding</p>
          <h1 className="font-[Montserrat] font-extrabold text-3xl text-[#0B1F3A] mt-2">Connect your payout account</h1>
          <div className="mt-6 rounded-xl bg-[#0B1F3A] text-white p-6">
            <div className="text-xs uppercase tracking-widest text-white/60">Account holder</div>
            <div className="font-[Montserrat] font-extrabold text-2xl mt-1" data-testid="mock-connect-name">
              {account.user_name || account.user_email}
            </div>
            <div className="mt-2 text-white/60 text-xs font-mono">{account.account_id}</div>
          </div>
          <ul className="mt-6 space-y-2 text-sm text-[#0B1F3A]/85">
            <li>✓ Identity verification (mock)</li>
            <li>✓ Bank account linkage (mock)</li>
            <li>✓ Tax information (mock)</li>
            <li>✓ Terms of Service (mock)</li>
          </ul>
          <p className="text-xs text-[#0B1F3A]/70 mt-4">
            No real KYC. Clicking <b>Complete onboarding</b> flips <code>payouts_enabled=true</code> on
            your Connect account so future revenue rows can be paid out.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button
              variant="secondary"
              onClick={() => finish("refresh")}
              disabled={!!busy}
              data-testid="mock-connect-refresh"
            >
              {busy === "refresh" ? "…" : "Come back later"}
            </Button>
            <Button
              variant="primary"
              onClick={() => finish("complete")}
              disabled={!!busy || account.payouts_enabled}
              data-testid="mock-connect-complete"
            >
              {busy === "complete"
                ? "Enabling…"
                : account.payouts_enabled
                  ? "Already enabled"
                  : "Complete onboarding"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
